import React from 'react';
import { motion } from 'framer-motion';
import { ContainerScroll } from './ContainerScroll';
import { useLanguage } from '../../context/LanguageContext';
import '../../styles/design-system.css';

/* ── Photos shown inside the 3D card ── */
const showcaseItems = [
  {
    label: 'Aconselhamento e Mentoria',
    img: 'https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=900&auto=format&fit=crop',
    span: 'col',
  },
  {
    label: 'Projeto Divas',
    img: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=900&auto=format&fit=crop',
    span: '',
  },
  {
    label: 'Educação Financeira',
    img: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?q=80&w=900&auto=format&fit=crop',
    span: '',
  },
  {
    label: 'Palestras e Treinamentos',
    img: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?q=80&w=900&auto=format&fit=crop',
    span: '',
  },
  {
    label: 'Missão e Propósito',
    img: 'https://images.unsplash.com/photo-1476820865390-c52aeebb9891?q=80&w=900&auto=format&fit=crop',
    span: '',
  },
];

/* ── Inner card content — grid with effects ── */
const CardContent = ({ theme }) => (
  <div
    style={{
      width: '100%',
      height: '100%',
      display: 'grid',
      gridTemplateColumns: '2fr 1fr 1fr',
      gridTemplateRows: '1fr 1fr',
      gap: 6,
      padding: 6,
      background: '#0d0d0d',
      position: 'relative',
    }}
  >
    {/* Scan-line effect overlay */}
    <div style={{
      position: 'absolute', inset: 0, zIndex: 10, pointerEvents: 'none', borderRadius: 12,
      background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.04) 2px, rgba(0,0,0,0.04) 4px)',
    }} />

    {/* Large featured image */}
    <motion.div
      style={{ gridRow: '1 / 3', position: 'relative', borderRadius: 12, overflow: 'hidden' }}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.4 }}
    >
      <img
        src={showcaseItems[0].img}
        alt={showcaseItems[0].label}
        style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.6s ease' }}
      />
      {/* Gradient */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.2) 55%, transparent 100%)',
      }} />
      {/* Gold top accent bar */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: 'linear-gradient(90deg, #D4AF37, #B8860B, #D4AF37)',
      }} />
      {/* Tag badge */}
      <span style={{
        position: 'absolute', top: 12, right: 12,
        background: 'rgba(212,175,55,0.18)', backdropFilter: 'blur(8px)',
        border: '1px solid rgba(212,175,55,0.4)',
        color: '#E8C96A', fontSize: '0.6rem', fontFamily: 'SF Mono, monospace',
        letterSpacing: '0.1em', textTransform: 'uppercase',
        padding: '3px 8px', borderRadius: 20,
      }}>
        DESTAQUE
      </span>
      {/* Label */}
      <div style={{ position: 'absolute', bottom: 14, left: 14 }}>
        <span style={{
          display: 'block', color: 'rgba(255,255,255,0.55)',
          fontFamily: 'SF Mono, monospace', fontSize: '0.55rem',
          letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4,
        }}>
          — Área Principal
        </span>
        <span style={{
          color: '#E8C96A', fontFamily: "'Cormorant Garamond', serif",
          fontSize: '1.15rem', fontWeight: 700,
          textShadow: '0 2px 12px rgba(0,0,0,0.6)',
        }}>
          {showcaseItems[0].label}
        </span>
      </div>
    </motion.div>

    {/* 4 smaller images with hover effects */}
    {showcaseItems.slice(1).map((item, idx) => (
      <motion.div
        key={item.label}
        style={{ position: 'relative', borderRadius: 10, overflow: 'hidden' }}
        whileHover={{ scale: 1.04, zIndex: 5 }}
        transition={{ duration: 0.35 }}
      >
        <img
          src={item.img}
          alt={item.label}
          style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s ease' }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        {/* Dark overlay */}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.1) 60%)' }} />
        {/* Shimmer on hover */}
        <motion.div
          initial={{ x: '-100%', opacity: 0 }}
          whileHover={{ x: '200%', opacity: 0.3 }}
          transition={{ duration: 0.6 }}
          style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.5) 50%, transparent 60%)',
            pointerEvents: 'none',
          }}
        />
        {/* Number badge */}
        <span style={{
          position: 'absolute', top: 8, right: 8,
          width: 20, height: 20, borderRadius: '50%',
          background: 'rgba(212,175,55,0.25)', border: '1px solid rgba(212,175,55,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#E8C96A', fontSize: '0.6rem', fontWeight: 700,
          fontFamily: 'SF Mono, monospace',
        }}>
          {idx + 1}
        </span>
        {/* Label */}
        <span style={{
          position: 'absolute', bottom: 7, left: 9,
          color: 'rgba(255,255,255,0.9)',
          fontFamily: 'Inter, sans-serif', fontSize: '0.65rem', fontWeight: 600,
          textShadow: '0 1px 6px rgba(0,0,0,0.8)',
        }}>
          {item.label}
        </span>
      </motion.div>
    ))}
  </div>
);


/* ── Section wrapper ── */
const HeroScrollSection = () => {
  const { theme } = useLanguage();
  const isDark = theme === 'dark';

  const titleComponent = (
    <div>
      <span
        className="mono-text"
        style={{ color: 'var(--accent-gold-400)', display: 'block', marginBottom: '0.75rem' }}
      >
        UMA JORNADA DE TRANSFORMAÇÃO
      </span>

      <h2
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 'clamp(2rem, 5vw, 3.5rem)',
          fontWeight: 700,
          color: isDark ? '#F5EDD6' : '#2C1A0E',
          lineHeight: 1.15,
          marginBottom: '1rem',
        }}
      >
        Descubra o impacto{' '}
        <br />
        <span className="gold-shimmer">de mais de 21 anos</span>{' '}
        de missão
      </h2>

      <p
        className="body-large"
        style={{
          color: 'var(--text-secondary)',
          maxWidth: 560,
          margin: '0 auto 2rem',
        }}
      >
        Palestras, aconselhamento, educação financeira e formação de líderes —
        tudo unificado por um propósito: transformar vidas pela Palavra de Deus.
      </p>

      <motion.a
        href="#contact"
        onClick={(e) => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}
        className="btn-primary"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}
        whileHover={{ scale: 1.04, y: -2 }}
        whileTap={{ scale: 0.97 }}
        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
      >
        Agende sua consultoria →
      </motion.a>
    </div>
  );

  return (
    <section
      style={{
        background: isDark
          ? 'linear-gradient(180deg, #0d0d0d 0%, #111 50%, #0d0d0d 100%)'
          : 'linear-gradient(180deg, #FAF7F2 0%, #F3EDE4 50%, #FAF7F2 100%)',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Radial glow behind card */}
      <div style={{
        position: 'absolute',
        top: '40%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 600, height: 400,
        background: isDark
          ? 'radial-gradient(ellipse, rgba(184,134,11,0.12) 0%, transparent 70%)'
          : 'radial-gradient(ellipse, rgba(184,134,11,0.08) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <ContainerScroll titleComponent={titleComponent}>
        <CardContent theme={theme} />
      </ContainerScroll>
    </section>
  );
};

export default HeroScrollSection;
